import React from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { LogoMark } from "@/components/Logo";
import { Download } from "lucide-react";

const ASSETS = [
  { key: "logo-color", label: "Color", desc: "Full-color mark with gold gradient. Use on light backgrounds.", bg: "#FFFFFF" },
  { key: "logo-mono-dark", label: "Mono · Dark", desc: "Single-color charcoal. Use in one-color print and stamps.", bg: "#F2EFEA" },
  { key: "logo-mono-light", label: "Mono · Light", desc: "Single-color off-white. Use on dark backgrounds and photos.", bg: "#0F1115" },
];

export default function SettingsBrand() {
  return (
    <DashboardLayout>
      <h1 className="text-2xl sm:text-3xl font-bold" data-testid="brand-heading">Brand kit</h1>
      <p className="mt-1 text-sm text-[#3B424B]">SVG variants of the TreadMargin mark for invoices, quotes and shop signage. Please keep clear space equal to at least one T-height around the mark.</p>

      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {ASSETS.map((a) => (
          <div key={a.key} className="tm-card p-5 bg-white flex flex-col" data-testid={`brand-asset-${a.key}`}>
            <div className="rounded-lg h-32 flex items-center justify-center" style={{ background: a.bg }}>
              <img src={`/brand/${a.key}.svg`} alt={a.label} width={80} height={80} />
            </div>
            <div className="mt-4 font-semibold">{a.label}</div>
            <div className="text-xs text-[#3B424B] mt-1 flex-1">{a.desc}</div>
            <div className="mt-4 flex gap-2">
              <a href={`/brand/${a.key}.svg`} download className="tm-btn-outline h-9 px-3 text-xs" data-testid={`brand-download-${a.key}`}><Download size={13} /> SVG</a>
              <a
                href="#"
                onClick={async (e) => {
                  e.preventDefault();
                  const res = await fetch(`/brand/${a.key}.svg`);
                  const text = await res.text();
                  const img = new Image();
                  const svgBlob = new Blob([text], { type: "image/svg+xml;charset=utf-8" });
                  const url = URL.createObjectURL(svgBlob);
                  img.onload = () => {
                    const canvas = document.createElement("canvas");
                    canvas.width = 512; canvas.height = 512;
                    const ctx = canvas.getContext("2d");
                    if (a.bg !== "transparent") { ctx.fillStyle = a.bg; ctx.fillRect(0, 0, 512, 512); }
                    ctx.drawImage(img, 64, 64, 384, 384);
                    canvas.toBlob((blob) => {
                      const a2 = document.createElement("a");
                      a2.href = URL.createObjectURL(blob);
                      a2.download = `${a.key}.png`;
                      a2.click();
                      URL.revokeObjectURL(a2.href);
                    }, "image/png");
                    URL.revokeObjectURL(url);
                  };
                  img.src = url;
                }}
                className="tm-btn-outline h-9 px-3 text-xs"
                data-testid={`brand-png-${a.key}`}
              ><Download size={13} /> PNG 512</a>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-8 tm-card p-6 bg-white max-w-2xl">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-wider text-[#3B424B] font-semibold">Download everything</div>
            <div className="mt-1 font-semibold">Full brand kit as a single zip</div>
            <p className="mt-1 text-xs text-[#3B424B]">Includes all three SVG variants, the favicon, the social card and a short usage README.</p>
          </div>
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/brand/kit.zip`} className="tm-btn-primary" data-testid="brand-download-zip"><Download size={14} /> Download brand-kit.zip</a>
        </div>
      </div>

      <div className="mt-8 tm-card p-6 bg-white max-w-2xl">
        <div className="text-xs uppercase tracking-wider text-[#3B424B] font-semibold">Usage guidelines</div>
        <ul className="mt-3 space-y-2 text-sm text-[#0F1115]">
          <li>· Keep clear space of at least one T-height around the mark.</li>
          <li>· Minimum size is 24px on screen and 12mm in print.</li>
          <li>· Do not recolour the gold gradient — use the mono variants for single-colour print.</li>
          <li>· Do not add drop-shadows, outlines or rotate the mark.</li>
          <li>· Every mark file lives under <code className="font-mono text-xs">/brand/*.svg</code>.</li>
        </ul>
      </div>
    </DashboardLayout>
  );
}
